<template>
  <app-modal
    class="register-modal"
    :shown="shown"
    @close="handleClose">
    <div class="center-register-modal">
      <h1>{{ modalTitle }}</h1>

      <div
        v-if="errorMessages.main"
        class="alert alert-danger">{{ errorMessages.main }}
      </div>

      <form
        name="form"
        @submit.prevent="handleCreateAccount"
        role="form"
        class="form-horizontal register-form">

        <div class="fields-line">
          <div class="field-item">
            <a-input
              :value="firstName"
              label="Name*"
              name="firstName"
              :required="isRequiredMessageShown('firstName')"
              @input="updateRegistrationData">
            </a-input>
          </div>
          <div class="field-item">
            <a-input
              :value="familyName"
              label="Last Name*"
              name="familyName"
              :required="isRequiredMessageShown('familyName')"
              @input="updateRegistrationData">
            </a-input>
          </div>
        </div>

        <div v-if="modalData.mode !== 'invite'" class="fields-line">
          <div class="field-item">
            <a-input
              :value="email"
              label="Email*"
              name="email"
              :error="errorMessages.email"
              :required="isRequiredMessageShown('email')"
              @input="updateRegistrationData">
            </a-input>
          </div>
          <div class="field-item">
            <a-input
              :value="confirmEmail"
              label="Confirm Email*"
              name="confirmEmail"
              :required="isRequiredMessageShown('confirmEmail')"
              :error="errorMessages.confirmEmail"
              @input="updateRegistrationData">
            </a-input>
          </div>
        </div>

        <div v-if="modalData.mode === 'invite'" class="fields-line">
          <div class="field-item preselected-block">
            <span class="preselected-label">
              Email*
            </span>
            <span class="preselected-data">
              {{ email }}
            </span>
          </div>
        </div>

        <div class="fields-line">
          <div class="field-item">
            <a-input
              :value="password"
              label="Password*"
              name="password"
              mode="password"
              :required="isRequiredMessageShown('password')"
              :error="errorMessages.password"
              @input="updateRegistrationData">
            </a-input>
          </div>
        </div>

        <div class="fields-line">
          <div class="field-item">
            <a-input-search
              :value="organisation"
              :options="searchCompanies"
              option-key="displayName"
              option-label="displayName"
              class="a-search"
              label="Organisation*"
              :required="isRequiredMessageShown('organisation')"
              @input="updateCompanyName"
              :isCard="true"
              ref="companyName" />
          </div>
          <div class="field-item">
            <a-select
              :value="{name: sector}"
              name="sector"
              :options="sectorOptions"
              option-key="name"
              option-label="name"
              class="a-select"
              label="Business Sector*"
              :required="isRequiredMessageShown('sector')"
              :isAutofocus="false"
              @input="updateRegistrationData">
            </a-select>
          </div>
        </div>

        <div class="fields-line">
          <div class="field-item field-item-wide">
            <app-checkbox
              :value="acceptTerms"
              name="acceptTerms"
              label='I accept Asoko Insight <a href="/about/legal" target="_blank">Terms & Conditions.</a><span>&ast;</span>'
              @change="updateRegistrationData">
            </app-checkbox>
          </div>
          <div class="field-item">
            <label>Fields above marked <span>&ast;</span> are required.</label>
          </div>
        </div>

        <div v-if="modalData.mode !== 'invite'" class="recaptcha-wrap">
          <vue-recaptcha
            :sitekey="sitekey"
            @verify="setRecaptcha"
            @expired="recaptcha=''">
          </vue-recaptcha>
        </div>

        <div class="form-actions">
          <button
            type="submit"
            :disabled="isLoading"
            class="cta-button">Create
          </button>
          <a
            v-if="modalData.mode !== 'invite'"
            @click="displayLogin"
            class="btn btn-link">Login as Existing User
          </a>
        </div>
      </form>
    </div>
  </app-modal>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import { mapActions } from 'vuex'
import { autocompleteCompany } from 'theme/mixins/autocomplete'
import appModal from 'components/common/app-modal.vue'
import aInput from 'components/a-input'
import aInputSearch from 'components/a-input-search'
import aSelect from 'ui-components/components/a-select'
import appCheckbox from 'ui-components/components/app-checkbox.vue'
import validation from 'src/services/validation.service'
import config from 'src/app.config'
import optionsData from 'src/data/options'

export default {
  name: 'modal-register',
  components: {
    appModal,
    VueRecaptcha,
    aInput,
    aInputSearch,
    aSelect,
    appCheckbox,
  },
  mixins: [autocompleteCompany],
  props: ['shown'],
  data() {
    return {
      firstName: '',
      familyName: '',
      password: '',
      email: '',
      confirmEmail: '',
      organisation: '',
      sector: '',
      acceptTerms: false,
      recaptcha: '',
      isLoading: false,
      errorMessages: {
        main: '',
        email: '',
        confirmEmail: '',
        password: '',
      },
      sectorOptions: optionsData.businessSectorOptions,
      sitekey: config.recaptchaApikey,
      isSubmitAttempt: false,
    }
  },
  computed: {
    modalData() {
      return this.$store.getters.modalData
    },
    modalTitle() {
      let title = 'Create New Account'
      if (this.modalData.mode === 'invite') {
        title = 'Account needed to access project'
      }
      return title
    },
  },
  created() {
    this.setDataFromStore()
  },
  watch: {
    modalData(value) {
      this.setDataFromStore()
    },
  },
  methods: {
    ...mapActions({
      showModal: 'showModal',
      createAccount: 'createAccount',
    }),
    handleClose() {
      this.errorMessages.main = ''
      this.isLoading = false
      this.$emit('close')
    },
    handleCreateAccount() {
      this.clearErrorMessages()
      this.isSubmitAttempt = true
      if (
        !this.firstName ||
        !this.familyName ||
        !this.email ||
        !this.confirmEmail ||
        !this.password ||
        !this.organisation ||
        !this.sector
      ) {
        this.errorMessages.main = 'Not all required data provided'
        return
      }
      if (!validation.isValidEmail(this.email)) {
        this.errorMessages.email = 'Email is invalid'
        return
      }
      if (this.email !== this.confirmEmail) {
        this.errorMessages.confirmEmail = "Emails don't match"
        return
      }
      if (this.password.length < 8) {
        this.errorMessages.password =
          'Password should have at least 8 characters'
        return
      }
      if (!this.acceptTerms) {
        this.errorMessages.main = 'You should accept our T&C to continue'
        return
      }
      if (!this.recaptcha && this.modalData.mode !== 'invite') {
        this.errorMessages.main = 'Recaptcha is required'
        return
      }
      this.isLoading = true
      const {
        email,
        password,
        firstName,
        familyName,
        confirmEmail,
        organisation,
        sector,
        acceptTerms,
        recaptcha,
      } = this
      const displayName = `${firstName} ${familyName}`
      const userName = displayName.toLowerCase()

      const analyticsUtm = _.pickBy(
        this.$route.query,
        (val, key) => _.startsWith(key, 'utm') && val
      )

      const newUser = {
        email,
        password,
        firstName,
        familyName,
        displayName,
        username: userName,
        confirmemail: confirmEmail,
        organisation,
        sector,
        acceptTerms,
        recaptcha,
        isSubmitAttempt: false,
        ...analyticsUtm,
      }

      if (this.modalData.mode === 'invite') {
        newUser.recaptcha = config.recaptchaValidResponse
      }

      this.createAccount(newUser)
        .then(res => {
          this.handleClose()
        })
        .catch(err => {
          this.isLoading = false
          this.errorMessages.main = err.response
            ? err.response.data.message
            : err.message
          this.$log(err)
        })
    },
    displayLogin() {
      this.showModal('login')
    },
    setRecaptcha(response) {
      this.recaptcha = response
    },
    isRequiredMessageShown(field) {
      const value = _.get(this, field)
      return this.isSubmitAttempt && _.isEmpty(value)
    },
    updateCompanyName(v) {
      if (v && typeof v === 'object') {
        if (v.displayName) {
          this.updateRegistrationData({
            value: v.displayName,
            name: 'organisation',
          })
        }
      } else {
        this.updateRegistrationData({ value: v, name: 'organisation' })
        this.autocompleteCompany(v)
      }
    },
    updateRegistrationData(v) {
      if (v.name === 'sector' && !v.value) {
        return
      }
      if (v.value !== undefined) {
        this[v.name] = v.value
      }
    },
    setDataFromStore() {
      if (this.modalData.mode === 'invite' && this.modalData.email) {
        this.email = this.modalData.email
        this.confirmEmail = this.modalData.email
      }
    },
    clearErrorMessages() {
      this.isSubmitAttempt = false
      this.errorMessages.main = ''
      this.errorMessages.email = ''
      this.errorMessages.confirmEmail = ''
      this.errorMessages.password = ''
    },
  },
}
</script>

<style lang="scss" scoped>
.register-modal h1 {
  margin-bottom: 30px;
}
.fields-line {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 15px;
  align-items: center;
}
.field-item {
  display: inline-block;
  width: 260px;
  margin-right: 30px;
  margin-bottom: 5px;
}
.field-item:last-child {
  margin-right: 0px;
}
.field-item-wide {
  width: 450px;
}
.preselected-block {
  display: flex;
  flex-direction: column;
  color: #95a1aa;
}
.preselected-label {
  font-size: 13px;
}
.preselected-data {
  border-bottom: 1px solid #95a1aa;
  padding-bottom: 5px;
  color: #777;
  background: #eee;
}
.form-actions {
  margin-top: $margin-m;
  text-align: center;
}
.a-select {
  width: 100%;
  & ::v-deep .v-dropdown {
    overflow-y: scroll;
    max-height: 270px;
  }
}
.a-search {
  width: 100%;
  & ::v-deep .v-dropdown {
    overflow-y: auto;
    max-height: 270px;
  }
}
@include tablet {
  .center-register-modal {
    padding: 0px 50px;
  }
  .register-modal ::v-deep .modal-content {
    width: 700px;
  }
}
</style>
